/*
 * The product pages' picture frames, and the MOGRT template pages' monitors.
 *
 * Steps: every [data-mogrt-step] shown on the page is one step of the story,
 * in page order. Each gets --n (its number) and --m (how many there are); the
 * transport under its picture parks the playhead at N/M and reads
 * 00:00:0N:00 of 00:00:0M:00 (CSS counters). A pinned story ([data-mogrt-stories]) hands its line the step of the
 * story showing, and marks that story data-active so the words cross-fade.
 * Steps hidden at this width (a section's phone and desktop layouts) don't
 * count, so the numbers match the chips (a CSS counter over the same
 * elements).
 *
 * Screens ([data-mogrt-screen]): a picture with a transparent background
 * gets a coloured surface behind it once it loads (data-tone on its
 * picture): a soft light one when any of what's drawn is dark lettering,
 * which would all but vanish on the dark frame, and a deep blue one when
 * it is all light or colourful. Photos keep the dark frame. A picture that
 * is mostly one tall thing (a panel of controls on a square canvas) marks
 * its monitor data-shape="tall", for a squarer screen it can fill.
 *
 * Every other picture on a product page (the sections below the product:
 * the preset pages' story monitors, bonus panes, image-with-text, collage)
 * gets the same surface when it is see-through, on its parent. The
 * product's own gallery puts its see-through pictures on Apple gray instead
 * (data-see-through on the slide and its preview). Other product pictures
 * (product cards, the comparison's box art) and small icons and logos are
 * left as they are. layout/theme.liquid loads this file on every product
 * page for that.
 *
 * Films ([data-mogrt-film]): the transport under the video runs with it
 * (--film-p, its timecode and length, and data-running once it plays). A
 * clip (a video file) shows no video controls, so its transport is the
 * control ([data-mogrt-scrub], a slider): press along the timeline to jump
 * there, drag the playhead to scrub - the picture follows, held still - and
 * let go to play on from there if it was playing. Arrow keys step a
 * second, Page Up/Down five, Home/End go to either end.
 */
(() => {
  if (window.mogrtMonitor) {
    window.mogrtMonitor.refresh();
    return;
  }

  /* ---- steps ---- */
  const shown = (element) => element.getClientRects().length > 0;

  const syncStories = (stories) => {
    // the story showing is the last one its scroller hasn't hidden
    const items = [...stories.querySelectorAll('[data-mogrt-step]')];
    const active = items.filter((item) => !item.classList.contains('opacity-0')).pop() || items[0];
    if (!active) return;
    const n = active.style.getPropertyValue('--n');
    if (n) stories.style.setProperty('--n', n);
    items.forEach((item) => item.toggleAttribute('data-active', item === active));
    stories.setAttribute('data-mogrt-live', '');
  };

  const number = () => {
    const steps = [...document.querySelectorAll('[data-mogrt-step]')].filter(shown);
    steps.forEach((step, index) => {
      step.style.setProperty('--n', String(index + 1));
      step.style.setProperty('--m', String(steps.length));
    });
    document.querySelectorAll('[data-mogrt-stories]').forEach((stories) => {
      stories.style.setProperty('--m', String(steps.length));
      syncStories(stories);
    });
    document.documentElement.toggleAttribute('data-mogrt-numbered', steps.length > 0);
  };

  const watched = new WeakSet();
  const watch = () => {
    document.querySelectorAll('[data-mogrt-stories]').forEach((stories) => {
      if (watched.has(stories)) return;
      watched.add(stories);
      new MutationObserver(() => syncStories(stories)).observe(stories, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true
      });
    });
  };

  // A pinned story sits centred in the screen below the header: its
  // sticky-element keeps data-gap px under the header (the scroller counts
  // from the same place).
  const wide = window.matchMedia('(min-width: 1024px)');
  const centre = () => {
    if (!wide.matches) return;
    const header = document.querySelector('.header-section header[is="sticky-header"]');
    const top = header ? header.getBoundingClientRect().height : 0;
    const gaps = [...document.querySelectorAll('sticky-element[data-mogrt-stories]')].map((stories) => [
      stories,
      String(Math.max(20, Math.round((window.innerHeight - top - stories.offsetHeight) / 2)))
    ]);
    gaps.forEach(([stories, gap]) => {
      if (stories.getAttribute('data-gap') !== gap) stories.setAttribute('data-gap', gap);
    });
  };

  /* ---- screens ---- */
  // what a picture needs: surface 'light' or 'color' when it is partly
  // see-through (else ''), and whether what's drawn is tall
  // (throws when the picture is from another origin and can't be read)
  const pixels = (image, size) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, size, size);
    return context.getImageData(0, 0, size, size).data;
  };

  const read = (image, source = image) => {
    try {
      const size = 64;
      const data = pixels(source, size);
      let clear = 0;
      let solid = 0;
      let dark = 0;
      let left = size;
      let right = -1;
      let top = size;
      let bottom = -1;
      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        if (alpha < 24) {
          clear += 1;
          continue;
        }
        solid += 1;
        const x = (i / 4) % size;
        const y = Math.floor(i / 4 / size);
        left = Math.min(left, x);
        right = Math.max(right, x);
        top = Math.min(top, y);
        bottom = Math.max(bottom, y);
        const luminance = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
        if (luminance < 0.25) dark += 1;
      }
      if (!solid) return { surface: '', tall: false };
      // what's drawn, in the picture's own proportions
      const width = ((right - left + 1) / size) * image.naturalWidth;
      const height = ((bottom - top + 1) / size) * image.naturalHeight;
      const tall = width / height < 0.8;
      // a real share of the picture is see-through
      if (clear / (size * size) <= 0.08) return { surface: '', tall };
      // even a little dark lettering needs the light surface
      return { surface: dark / solid > 0.03 ? 'light' : 'color', tall };
    } catch (error) {
      // a picture from another origin can't be read here: leave the dark
      // frame unless a readable copy says otherwise
      return { surface: '', tall: image.naturalWidth / image.naturalHeight < 0.8, unread: source === image };
    }
  };

  // a monitor turned squarer: a pinned story's scroller measures its
  // pictures once, so it measures them again
  const reshape = (shot) => {
    if (shot.getAttribute('data-shape') === 'tall') return;
    shot.setAttribute('data-shape', 'tall');
    const banner = shot.closest('scrolling-banner');
    if (banner && banner.initialized && banner.mql?.matches) banner.setMediaHeight?.();
    centre();
  };

  // the product's own pictures, and what isn't a picture of the page's
  // story (icons, logos, avatars)
  const PLAIN = [
    '[id$="__main-product"]',
    'media-gallery',
    'product-recommendations',
    'recently-viewed',
    '.product-card',
    '[class*="upp-compare"]',
    'header',
    'footer',
    '.cart-drawer',
    '[data-mogrt-plain]'
  ].join(',');
  const pictured = (image) =>
    !!image.closest('[data-mogrt-screen]') ||
    (document.body.classList.contains('template-product') &&
      !!image.closest('main') &&
      !image.closest(PLAIN) &&
      image.naturalWidth >= 240 &&
      image.naturalHeight >= 160);

  // a picture served from another origin is read again from a copy that
  // asks for it openly (the CDN allows that); if that fails too it keeps
  // the dark frame
  const openReads = new Map();
  const readOpenly = (image) => {
    const src = image.currentSrc || image.src;
    const key = `${src}\n${image.naturalWidth}x${image.naturalHeight}`;
    if (openReads.has(key)) return openReads.get(key);
    const pending = new Promise((resolve) => {
      const copy = new Image();
      copy.crossOrigin = 'anonymous';
      copy.decoding = 'async';
      copy.onload = () => resolve(read(image, copy));
      copy.onerror = () => resolve(null);
      copy.src = src;
    }).then((found) => {
      if (found) return found;
      if (openReads.get(key) === pending) openReads.delete(key);
      return { surface: '', tall: false };
    });
    openReads.set(key, pending);
    if (openReads.size > 64) openReads.delete(openReads.keys().next().value);
    return pending;
  };

  // The product's own gallery: a see-through slide (a box shot, a panel cut
  // out with its window shadow) stands on Apple gray, as the first picture
  // does, and so does its preview (data-see-through, premiere-product.css).
  // Slide and preview are one picture, so whichever of them comes in first
  // decides for both: a later slide is gray before its picture arrives.
  const grounds = new Map(); // media id -> see-through
  const galleryFrame = (image) =>
    image.closest('.product-hero media-gallery') ? image.closest('[data-media-id]') : null;
  const ground = (frame, seeThrough) => {
    const id = frame.getAttribute('data-media-id');
    grounds.set(id, seeThrough);
    if (!seeThrough) return;
    frame.closest('media-gallery').querySelectorAll(`[data-media-id="${id}"]`).forEach((other) => {
      other.setAttribute('data-see-through', '');
    });
  };

  const tone = async (image) => {
    if (image.dataset.mogrtToned) return;
    const frame = galleryFrame(image);
    if (!image.complete || !image.naturalWidth || !(frame || pictured(image))) return;
    image.dataset.mogrtToned = '';
    if (frame && grounds.has(frame.getAttribute('data-media-id'))) {
      if (grounds.get(frame.getAttribute('data-media-id'))) frame.setAttribute('data-see-through', '');
      return;
    }
    let found = read(image);
    if (found.unread) found = await readOpenly(image);
    if (frame) {
      ground(frame, !!found.surface);
      return;
    }
    const { surface, tall } = found;
    if (surface) image.parentElement.setAttribute('data-tone', surface);
    const shot = image.closest('.mogrt-shot');
    if (tall && shot) reshape(shot);
  };

  document.addEventListener('load', (event) => {
    if (event.target instanceof HTMLImageElement) tone(event.target);
  }, true);

  /* ---- films ---- */
  const FPS = 30;
  const pad = (value) => String(value).padStart(2, '0');
  const timecode = (seconds) => {
    const frames = Math.max(0, Math.floor(seconds * FPS));
    const whole = Math.floor(frames / FPS);
    return `${pad(Math.floor(whole / 3600))}:${pad(Math.floor(whole / 60) % 60)}:${pad(whole % 60)}:${pad(frames % FPS)}`;
  };
  const write = (element, text) => {
    if (element && element.textContent !== text) element.textContent = text;
  };
  const mark = (element, name, value) => {
    if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
  };
  const clock = (seconds) => `${Math.floor(seconds / 60)}:${pad(Math.floor(seconds % 60))}`;
  const timed = (video) => Number.isFinite(video?.duration) && video.duration > 0;

  // the film's clip: its phone or desktop copy, whichever shows
  const clipOf = (film) => {
    const videos = [...film.querySelectorAll('video')];
    return videos.find((video) => video.getClientRects().length > 0) || videos[0] || null;
  };

  // the transport at a moment of the clip: playhead, timecode, and the
  // slider's value
  const show = (film, video, time) => {
    film.style.setProperty('--film-p', (time / video.duration).toFixed(4));
    write(film.querySelector('[data-mogrt-now]'), timecode(time));
    const scrub = film.querySelector('[data-mogrt-scrub]');
    mark(scrub, 'aria-valuemax', String(Math.floor(video.duration)));
    mark(scrub, 'aria-valuenow', String(Math.floor(time)));
    mark(scrub, 'aria-valuetext', clock(time));
  };

  // One seek at a time: while the picture is still finding a frame, the
  // newest position waits for it, so a drag shows frames all the way
  // instead of each seek cancelling the last.
  const seeks = new WeakMap();
  const seek = (video, time) => {
    const state = seeks.get(video) || {};
    state.want = time;
    seeks.set(video, state);
    if (state.sent === undefined || !video.seeking) {
      state.sent = time;
      video.currentTime = time;
    }
  };
  const landed = (video) => {
    const state = seeks.get(video);
    if (!state) return;
    if (state.want !== state.sent) {
      state.sent = state.want;
      video.currentTime = state.want;
    } else {
      seeks.delete(video);
    }
  };

  let drag = null;

  const filmTime = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const film = video.closest('[data-mogrt-film]');
    if (!film || !timed(video)) return;
    if (event.type === 'seeked') landed(video);
    write(film.querySelector('[data-mogrt-end]'), timecode(video.duration));
    film.setAttribute('data-timed', '');
    if (event.type !== 'loadedmetadata' && event.type !== 'durationchange') film.setAttribute('data-running', '');
    // while the playhead is held, or a scrub's seek is on its way, the
    // transport shows the playhead's place, not the frame showing yet
    if (drag?.video === video || seeks.has(video)) return;
    show(film, video, video.currentTime);
  };

  ['timeupdate', 'seeked', 'loadedmetadata', 'durationchange'].forEach((type) => {
    document.addEventListener(type, filmTime, true);
  });

  const scrubTo = (film, video, time) => {
    show(film, video, time);
    seek(video, time);
  };
  const along = (scrub, x) => {
    const rect = scrub.getBoundingClientRect();
    return rect.width > 0 ? Math.min(1, Math.max(0, (x - rect.left) / rect.width)) : 0;
  };

  document.addEventListener('pointerdown', (event) => {
    const scrub = event.target.closest?.('[data-mogrt-scrub]');
    if (!scrub || !event.isPrimary || event.button !== 0) return;
    const film = scrub.closest('[data-mogrt-film]');
    const video = film && clipOf(film);
    if (!timed(video)) return;
    drag = { id: event.pointerId, scrub, film, video, playing: !video.paused };
    scrub.setPointerCapture?.(event.pointerId);
    film.setAttribute('data-scrubbing', '');
    if (!video.paused) video.pause();
    scrubTo(film, video, along(scrub, event.clientX) * video.duration);
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    scrubTo(drag.film, drag.video, along(drag.scrub, event.clientX) * drag.video.duration);
  });

  const letGo = (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const { scrub, film, video, playing } = drag;
    if (event.type === 'pointerup') scrubTo(film, video, along(scrub, event.clientX) * video.duration);
    drag = null;
    film.removeAttribute('data-scrubbing');
    // play on, unless it was put away meanwhile (scrolled off screen)
    if (playing && video.closest('video-media')?.wantsPlay !== false) video.play().catch(() => {});
  };
  ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => {
    document.addEventListener(type, letGo);
  });

  const STEPS = new Map([['ArrowLeft', -1], ['ArrowDown', -1], ['ArrowRight', 1], ['ArrowUp', 1], ['PageDown', -5], ['PageUp', 5]]);
  document.addEventListener('keydown', (event) => {
    const scrub = event.target.closest?.('[data-mogrt-scrub]');
    if (!scrub || event.altKey || event.ctrlKey || event.metaKey) return;
    const film = scrub.closest('[data-mogrt-film]');
    const video = film && clipOf(film);
    if (!timed(video)) return;
    let time;
    if (STEPS.has(event.key)) time = (seeks.get(video)?.want ?? video.currentTime) + STEPS.get(event.key);
    else if (event.key === 'Home') time = 0;
    else if (event.key === 'End') time = video.duration;
    else return;
    event.preventDefault();
    scrubTo(film, video, Math.min(video.duration, Math.max(0, time)));
  });

  const refresh = (root = document) => {
    number();
    watch();
    centre();
    root.querySelectorAll(document.body.classList.contains('template-product') ? 'main img' : '[data-mogrt-screen] img').forEach(tone);
  };

  window.mogrtMonitor = { refresh };
  refresh();

  wide.addEventListener?.('change', () => {
    number();
    centre();
  });
  let resizing = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizing);
    resizing = requestAnimationFrame(centre);
  });
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(number));
})();

